import { Vector3 } from 'three';
import { SweepMesh } from '../geometry/sweep';
import { TrackRuntime } from '../track/runtime';
import { TrackSupportPrimitive } from '../supports/types';
import { boxPrism, jointBasis, JOINT_CLEARANCE_MM, trackSpineJointOrigin } from './joints';
import { footerPadHalfSide, footerThicknessM } from './supportPrims';
import { PrintSettings, printMmToWorldMetres } from './types';

/** Per-side drop-in clearance so a printed footer pad seats in the clip. */
export const GROUND_CLIP_CLEARANCE_MM = 0.4;
/** Clip wall thickness in print millimetres. */
export const GROUND_CLIP_WALL_MM = 1.2;
/** Extra XZ margin around the coaster footprint in print millimetres. */
export const GROUND_PLANE_MARGIN_MM = 10;
/** Shrink tiles vs the build cube so clips still fit. */
const GROUND_TILE_SLACK_MM = 4;
/** How far a clip unions down into the slab (world m). */
const SLAB_UNION_OVERLAP_M = 0.03;
/** Butterfly key length across the seam (print mm, end to end). */
export const GROUND_BUTTERFLY_LENGTH_MM = 12;
/** Wide end width along the seam (print mm). */
export const GROUND_BUTTERFLY_END_MM = 8;
/** Narrow waist width on the seam (print mm). */
export const GROUND_BUTTERFLY_WAIST_MM = 3;
/** Cutter overshoot past the slab top so the pocket stays open. */
const POCKET_MOUTH_OVERSHOOT_M = 0.04;

export interface GroundClip {
  x: number;
  z: number;
  /** Inner half-side of the square well (footer pad + clearance). */
  innerHalf: number;
  /** Outer half-side including clip walls. */
  outerHalf: number;
  /** Wall height above the slab top (= footer pad thickness). */
  height: number;
}

export interface GroundTileRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Butterfly / bow-tie key centered on a tile seam. */
export interface GroundButterflyKey {
  origin: Vector3;
  /** Unit axis across the seam (into one tile). */
  across: Vector3;
  /** Unit axis along the seam. */
  along: Vector3;
}

export interface GroundTileDraft {
  id: string;
  rect: GroundTileRect;
  clips: GroundClip[];
  keys: GroundButterflyKey[];
  meshes: SweepMesh[];
  subtract: SweepMesh[];
}

export interface GroundPlaneBuild {
  tiles: GroundTileDraft[];
  connectorKey: SweepMesh | null;
  footprint: GroundTileRect | null;
  slabTopY: number;
  slabBottomY: number;
  log: string[];
  warnings: string[];
}

export interface GroundPlaneFootprint {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Slab top — footer undersides sit on this plane. */
  slabTopY: number;
}

/** Slab thickness in world metres. */
export function groundPlaneThicknessM(settings: PrintSettings): number {
  return Math.max(printMmToWorldMetres(settings.groundPlaneHeightMm), 0.08);
}

/** Butterfly pocket depth in world metres (may exceed slab thickness → through cut). */
export function groundButterflyDepthM(settings: PrintSettings): number {
  return Math.max(printMmToWorldMetres(settings.groundButterflyDepthMm), 0.04);
}

/** Per-side drop-in gap in world metres. */
export function groundClipClearanceM(): number {
  return printMmToWorldMetres(GROUND_CLIP_CLEARANCE_MM);
}

export function groundClipWallM(): number {
  return printMmToWorldMetres(GROUND_CLIP_WALL_MM);
}

export function groundPlaneMarginM(): number {
  return printMmToWorldMetres(GROUND_PLANE_MARGIN_MM);
}

export function groundButterflySizeM(): {
  halfLength: number;
  halfEnd: number;
  halfWaist: number;
} {
  return {
    halfLength: printMmToWorldMetres(GROUND_BUTTERFLY_LENGTH_MM) * 0.5,
    halfEnd: printMmToWorldMetres(GROUND_BUTTERFLY_END_MM) * 0.5,
    halfWaist: printMmToWorldMetres(GROUND_BUTTERFLY_WAIST_MM) * 0.5,
  };
}

export function footerClipFromPad(
  pos: Vector3,
  radius: number,
  height: number,
): GroundClip {
  const innerHalf = footerPadHalfSide(radius) + groundClipClearanceM();
  const outerHalf = innerHalf + groundClipWallM();
  return {
    x: pos.x,
    z: pos.z,
    innerHalf,
    outerHalf,
    height: Math.max(height, 0.12),
  };
}

/**
 * XZ AABB of the coaster plus clip/margin padding. Slab top matches the lowest
 * footer underside (or Y=0 when there are no pads).
 */
export function groundPlaneFootprint(
  runtime: TrackRuntime,
  primitives: TrackSupportPrimitive[],
  settings: PrintSettings,
): GroundPlaneFootprint | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let slabTopY = 0;
  let found = false;
  let foundFooter = false;

  const addPoint = (x: number, z: number, pad = 0) => {
    found = true;
    minX = Math.min(minX, x - pad);
    maxX = Math.max(maxX, x + pad);
    minZ = Math.min(minZ, z - pad);
    maxZ = Math.max(maxZ, z + pad);
  };

  for (const f of runtime.frames) {
    addPoint(f.railPos.x, f.railPos.z);
  }
  for (const p of primitives) {
    switch (p.kind) {
      case 'footer': {
        const clip = footerClipFromPad(p.pos, p.radius, p.height);
        addPoint(clip.x, clip.z, clip.outerHalf);
        if (!foundFooter || p.pos.y < slabTopY) slabTopY = p.pos.y;
        foundFooter = true;
        break;
      }
      case 'column':
      case 'brace':
      case 'knee':
        addPoint(p.a.x, p.a.z);
        addPoint(p.b.x, p.b.z);
        break;
      case 'stub':
        addPoint(p.a.x, p.a.z);
        addPoint(p.b.x, p.b.z);
        break;
      case 'joint':
        addPoint(p.pos.x, p.pos.z);
        break;
      default: {
        const _never: never = p;
        void _never;
      }
    }
  }

  if (!found || !Number.isFinite(minX)) return null;
  if (!foundFooter) slabTopY = 0;

  const margin = groundPlaneMarginM();
  return {
    minX: minX - margin,
    maxX: maxX + margin,
    minZ: minZ - margin,
    maxZ: maxZ + margin,
    slabTopY,
  };
}

/** Equal-length splits so every interval is ≤ `maxSpan`. */
export function splitAxisEven(min: number, max: number, maxSpan: number): number[] {
  const span = max - min;
  if (!(maxSpan > 1e-6) || span <= maxSpan + 1e-6) return [min, max];
  const n = Math.max(2, Math.ceil(span / maxSpan - 1e-9));
  const lines = [min];
  for (let i = 1; i < n; i++) lines.push(min + (span * i) / n);
  lines.push(max);
  return lines;
}

export function groundTilesFromFootprint(
  footprint: GroundPlaneFootprint,
  settings: PrintSettings,
  _clips: GroundClip[] = [],
): GroundTileRect[] {
  void _clips;
  const tileM = printMmToWorldMetres(Math.max(settings.chunkSizeMm - GROUND_TILE_SLACK_MM, 40));
  const xLines = splitAxisEven(footprint.minX, footprint.maxX, tileM);
  const zLines = splitAxisEven(footprint.minZ, footprint.maxZ, tileM);
  const tiles: GroundTileRect[] = [];
  for (let i = 0; i < xLines.length - 1; i++) {
    for (let j = 0; j < zLines.length - 1; j++) {
      const minX = xLines[i]!;
      const maxX = xLines[i + 1]!;
      const minZ = zLines[j]!;
      const maxZ = zLines[j + 1]!;
      if (maxX - minX < 1e-4 || maxZ - minZ < 1e-4) continue;
      tiles.push({ minX, maxX, minZ, maxZ });
    }
  }
  return tiles;
}

function padId(n: number): string {
  return String(n).padStart(3, '0');
}

export function intervalsOverlap(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 - 1e-9 && b0 < a1 - 1e-9;
}

/** True when the clip square overlaps the tile in XZ. */
export function clipOverlapsTile(clip: GroundClip, rect: GroundTileRect): boolean {
  return (
    intervalsOverlap(clip.x - clip.outerHalf, clip.x + clip.outerHalf, rect.minX, rect.maxX) &&
    intervalsOverlap(clip.z - clip.outerHalf, clip.z + clip.outerHalf, rect.minZ, rect.maxZ)
  );
}

/** Axis-aligned box with Y along the extrusion, X/Z as the cross-section. */
export function aabbPrism(min: Vector3, max: Vector3): SweepMesh {
  const cx = (min.x + max.x) * 0.5;
  const cz = (min.z + max.z) * 0.5;
  const a = new Vector3(cx, min.y, cz);
  const b = new Vector3(cx, max.y, cz);
  const basis = jointBasis(a, new Vector3(0, 1, 0), new Vector3(1, 0, 0), new Vector3(0, 0, 1));
  return boxPrism(a, b, basis, Math.max((max.x - min.x) * 0.5, 1e-5), Math.max((max.z - min.z) * 0.5, 1e-5));
}

function intersectWallWithTile(
  min: Vector3,
  max: Vector3,
  tile: GroundTileRect,
): { min: Vector3; max: Vector3 } | null {
  const x0 = Math.max(min.x, tile.minX);
  const x1 = Math.min(max.x, tile.maxX);
  const z0 = Math.max(min.z, tile.minZ);
  const z1 = Math.min(max.z, tile.maxZ);
  if (x1 - x0 < 1e-5 || z1 - z0 < 1e-5) return null;
  return {
    min: new Vector3(x0, min.y, z0),
    max: new Vector3(x1, max.y, z1),
  };
}

/**
 * Square clip walls around a footer. When `tile` is set, only the portion that
 * lies on that tile is emitted so overlapping chunks share a split clip.
 */
export function footerClipWallMeshes(
  clip: GroundClip,
  slabTopY: number,
  tile?: GroundTileRect,
): SweepMesh[] {
  const y0 = slabTopY - SLAB_UNION_OVERLAP_M;
  const y1 = slabTopY + clip.height;
  const { x: cx, z: cz, innerHalf: inner, outerHalf: outer } = clip;
  const boxes: [Vector3, Vector3][] = [
    [new Vector3(cx + inner, y0, cz - outer), new Vector3(cx + outer, y1, cz + outer)],
    [new Vector3(cx - outer, y0, cz - outer), new Vector3(cx - inner, y1, cz + outer)],
    [new Vector3(cx - inner, y0, cz + inner), new Vector3(cx + inner, y1, cz + outer)],
    [new Vector3(cx - inner, y0, cz - outer), new Vector3(cx + inner, y1, cz - inner)],
  ];
  const out: SweepMesh[] = [];
  for (const [min, max] of boxes) {
    const clipped = tile ? intersectWallWithTile(min, max, tile) : { min, max };
    if (!clipped) continue;
    out.push(aabbPrism(clipped.min, clipped.max));
  }
  return out;
}

function keyOverlapsClip(
  origin: Vector3,
  across: Vector3,
  along: Vector3,
  clips: GroundClip[],
): boolean {
  const size = groundButterflySizeM();
  const clearance = printMmToWorldMetres(JOINT_CLEARANCE_MM);
  const halfLen = size.halfLength + clearance;
  const halfAlong = size.halfEnd + clearance;
  const x0 = origin.x - Math.abs(across.x) * halfLen - Math.abs(along.x) * halfAlong;
  const x1 = origin.x + Math.abs(across.x) * halfLen + Math.abs(along.x) * halfAlong;
  const z0 = origin.z - Math.abs(across.z) * halfLen - Math.abs(along.z) * halfAlong;
  const z1 = origin.z + Math.abs(across.z) * halfLen + Math.abs(along.z) * halfAlong;
  for (const c of clips) {
    if (
      intervalsOverlap(x0, x1, c.x - c.outerHalf, c.x + c.outerHalf) &&
      intervalsOverlap(z0, z1, c.z - c.outerHalf, c.z + c.outerHalf)
    ) {
      return true;
    }
  }
  return false;
}

function pickEdgeStation(
  along0: number,
  along1: number,
  halfAlong: number,
  blocked: (mid: number) => boolean,
): number | null {
  const span = along1 - along0;
  if (span < halfAlong * 4) return null;
  const candidates = [0.5, 0.35, 0.65, 0.25, 0.75, 0.2, 0.8];
  for (const t of candidates) {
    const mid = along0 + span * t;
    if (mid - halfAlong < along0 + 1e-4 || mid + halfAlong > along1 - 1e-4) continue;
    if (!blocked(mid)) return mid;
  }
  return null;
}

function addXSeamKeys(
  left: GroundTileDraft,
  right: GroundTileDraft,
  slabTopY: number,
  clips: GroundClip[],
): void {
  const x = left.rect.maxX;
  if (Math.abs(x - right.rect.minX) > 1e-4) return;
  const z0 = Math.max(left.rect.minZ, right.rect.minZ);
  const z1 = Math.min(left.rect.maxZ, right.rect.maxZ);
  const size = groundButterflySizeM();
  const mid = pickEdgeStation(z0, z1, size.halfEnd, (z) =>
    keyOverlapsClip(new Vector3(x, slabTopY, z), new Vector3(1, 0, 0), new Vector3(0, 0, 1), clips),
  );
  if (mid == null) return;
  const key: GroundButterflyKey = {
    origin: new Vector3(x, slabTopY, mid),
    across: new Vector3(1, 0, 0),
    along: new Vector3(0, 0, 1),
  };
  left.keys.push(key);
  right.keys.push(key);
}

function addZSeamKeys(
  south: GroundTileDraft,
  north: GroundTileDraft,
  slabTopY: number,
  clips: GroundClip[],
): void {
  const z = south.rect.maxZ;
  if (Math.abs(z - north.rect.minZ) > 1e-4) return;
  const x0 = Math.max(south.rect.minX, north.rect.minX);
  const x1 = Math.min(south.rect.maxX, north.rect.maxX);
  const size = groundButterflySizeM();
  const mid = pickEdgeStation(x0, x1, size.halfEnd, (x) =>
    keyOverlapsClip(new Vector3(x, slabTopY, z), new Vector3(0, 0, 1), new Vector3(1, 0, 0), clips),
  );
  if (mid == null) return;
  const key: GroundButterflyKey = {
    origin: new Vector3(mid, slabTopY, z),
    across: new Vector3(0, 0, 1),
    along: new Vector3(1, 0, 0),
  };
  south.keys.push(key);
  north.keys.push(key);
}

function wireTileKeys(
  drafts: GroundTileDraft[],
  slabTopY: number,
  clips: GroundClip[],
): void {
  const size = groundButterflySizeM();
  const minOverlap = size.halfEnd * 4;
  for (let i = 0; i < drafts.length; i++) {
    for (let k = i + 1; k < drafts.length; k++) {
      const a = drafts[i]!;
      const b = drafts[k]!;
      const shareX =
        Math.abs(a.rect.maxX - b.rect.minX) < 1e-4 || Math.abs(b.rect.maxX - a.rect.minX) < 1e-4;
      const shareZ =
        Math.abs(a.rect.maxZ - b.rect.minZ) < 1e-4 || Math.abs(b.rect.maxZ - a.rect.minZ) < 1e-4;
      const overlapZ = Math.min(a.rect.maxZ, b.rect.maxZ) - Math.max(a.rect.minZ, b.rect.minZ);
      const overlapX = Math.min(a.rect.maxX, b.rect.maxX) - Math.max(a.rect.minX, b.rect.minX);
      if (shareX && overlapZ > minOverlap) {
        const left = a.rect.maxX < b.rect.maxX ? a : b;
        const right = left === a ? b : a;
        addXSeamKeys(left, right, slabTopY, clips);
      } else if (shareZ && overlapX > minOverlap) {
        const south = a.rect.maxZ < b.rect.maxZ ? a : b;
        const north = south === a ? b : a;
        addZSeamKeys(south, north, slabTopY, clips);
      }
    }
  }
}

/**
 * Double-dovetail / bow-tie outline in the (across, along) plane, CCW from +Y.
 * Wide ends sit in each plate; the waist lies on the seam.
 */
export function butterflyOutline(
  halfLength: number,
  halfEnd: number,
  halfWaist: number,
): [number, number][] {
  const L = Math.max(halfLength, 1e-4);
  const E = Math.max(halfEnd, 1e-4);
  const W = Math.min(Math.max(halfWaist, 1e-4), E - 1e-4);
  return [
    [-L, -E],
    [0, -W],
    [L, -E],
    [L, E],
    [0, W],
    [-L, E],
  ];
}

function xzPoint(origin: Vector3, across: Vector3, along: Vector3, u: number, v: number): Vector3 {
  return new Vector3(
    origin.x + across.x * u + along.x * v,
    origin.y,
    origin.z + across.z * u + along.z * v,
  );
}

/**
 * Solid butterfly prism between `y0` and `y1`. One watertight shell (no internal
 * waist wall) so Manifold can subtract it as a pocket cutter.
 */
export function butterflyPrism(
  origin: Vector3,
  across: Vector3,
  along: Vector3,
  y0: number,
  y1: number,
  halfLength: number,
  halfEnd: number,
  halfWaist: number,
): SweepMesh {
  const outline = butterflyOutline(halfLength, halfEnd, halfWaist);
  const n = outline.length;
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (const y of [y0, y1]) {
    for (const [u, v] of outline) {
      const p = xzPoint(origin, across, along, u, v);
      positions.push(p.x, y, p.z);
      normals.push(0, y >= y1 ? 1 : -1, 0);
    }
  }

  for (let i = 0; i < n; i++) {
    const i1 = (i + 1) % n;
    indices.push(i, n + i, i1, i1, n + i, n + i1);
  }

  // Two convex quads per cap so the concave waist stays inside the bow-tie.
  // Left: 0-1-4-5  Right: 1-2-3-4  (CCW from +Y).
  const capQuad = (base: number, a: number, b: number, c: number, d: number, reverse: boolean) => {
    if (reverse) {
      indices.push(base + a, base + c, base + b, base + a, base + d, base + c);
    } else {
      indices.push(base + a, base + b, base + c, base + a, base + c, base + d);
    }
  };
  capQuad(0, 0, 1, 4, 5, true);
  capQuad(0, 1, 2, 3, 4, true);
  capQuad(n, 0, 1, 4, 5, false);
  capQuad(n, 1, 2, 3, 4, false);

  return {
    positions: Float32Array.from(positions),
    normals: Float32Array.from(normals),
    indices: Uint32Array.from(indices),
  };
}

export function butterflyCutterMesh(
  key: GroundButterflyKey,
  settings: PrintSettings,
  slabTopY: number,
  slabBottomY: number,
): SweepMesh {
  const size = groundButterflySizeM();
  const clearance = printMmToWorldMetres(JOINT_CLEARANCE_MM);
  const depth = groundButterflyDepthM(settings);
  const thickness = slabTopY - slabBottomY;
  const through = depth >= thickness - 1e-4;
  const y1 = slabTopY + POCKET_MOUTH_OVERSHOOT_M;
  const y0 = through ? slabBottomY - POCKET_MOUTH_OVERSHOOT_M : slabTopY - depth;
  return butterflyPrism(
    key.origin,
    key.across,
    key.along,
    y0,
    y1,
    size.halfLength + clearance,
    size.halfEnd + clearance,
    size.halfWaist + clearance,
  );
}

export function butterflyKeyMesh(
  origin: Vector3,
  across: Vector3,
  along: Vector3,
  settings: PrintSettings,
): SweepMesh {
  const size = groundButterflySizeM();
  const depth = Math.min(groundButterflyDepthM(settings), groundPlaneThicknessM(settings));
  const clearance = printMmToWorldMetres(0.1);
  const height = Math.max(depth - clearance, printMmToWorldMetres(0.3));
  return butterflyPrism(
    origin,
    across,
    along,
    origin.y,
    origin.y + height,
    size.halfLength,
    size.halfEnd,
    size.halfWaist,
  );
}

function spareButterflyKeyMesh(runtime: TrackRuntime, settings: PrintSettings): SweepMesh {
  const f = runtime.frameAt(0);
  const origin = trackSpineJointOrigin(f).add(f.right.clone().multiplyScalar(7));
  return butterflyKeyMesh(origin, f.tan, f.right, settings);
}

/**
 * Build chunked ground-plane drafts (slab + footer clips + butterfly key pockets).
 * Callers solidify each tile the same way as other print parts.
 */
export function buildGroundPlane(
  runtime: TrackRuntime,
  primitives: TrackSupportPrimitive[],
  settings: PrintSettings,
): GroundPlaneBuild {
  const log: string[] = [];
  const warnings: string[] = [];
  if (!settings.groundPlaneEnabled) {
    return {
      tiles: [],
      connectorKey: null,
      footprint: null,
      slabTopY: 0,
      slabBottomY: 0,
      log,
      warnings,
    };
  }

  const footprint = groundPlaneFootprint(runtime, primitives, settings);
  if (!footprint) {
    log.push('Ground plane: skipped (empty footprint)');
    return {
      tiles: [],
      connectorKey: null,
      footprint: null,
      slabTopY: 0,
      slabBottomY: 0,
      log,
      warnings,
    };
  }

  const thickness = groundPlaneThicknessM(settings);
  const slabTopY = footprint.slabTopY;
  const slabBottomY = slabTopY - thickness;
  const clips: GroundClip[] = [];
  for (const p of primitives) {
    if (p.kind !== 'footer') continue;
    const clip = footerClipFromPad(p.pos, p.radius, p.height);
    clip.height = Math.max(p.height, footerThicknessM(settings));
    clips.push(clip);
  }

  const rects = groundTilesFromFootprint(footprint, settings, clips);
  const drafts: GroundTileDraft[] = rects.map((rect, i) => ({
    id: `ground-${padId(i + 1)}`,
    rect,
    clips: [],
    keys: [],
    meshes: [],
    subtract: [],
  }));

  for (const clip of clips) {
    let assigned = 0;
    for (const draft of drafts) {
      if (!clipOverlapsTile(clip, draft.rect)) continue;
      draft.clips.push(clip);
      assigned += 1;
    }
    if (assigned === 0 && drafts.length > 0) {
      drafts[0]!.clips.push(clip);
    }
  }

  wireTileKeys(drafts, slabTopY, clips);

  for (const tile of drafts) {
    tile.meshes.push(
      aabbPrism(
        new Vector3(tile.rect.minX, slabBottomY, tile.rect.minZ),
        new Vector3(tile.rect.maxX, slabTopY, tile.rect.maxZ),
      ),
    );
    for (const clip of tile.clips) {
      tile.meshes.push(...footerClipWallMeshes(clip, slabTopY, tile.rect));
    }
    for (const key of tile.keys) {
      tile.subtract.push(butterflyCutterMesh(key, settings, slabTopY, slabBottomY));
    }
  }

  const nKeys = drafts.reduce((n, t) => n + t.keys.length, 0);
  log.push(
    `Ground plane: ${drafts.length} tile(s) slab=${settings.groundPlaneHeightMm}mm ` +
      `butterflyDepth=${settings.groundButterflyDepthMm}mm clips=${clips.length} keys=${nKeys} ` +
      `footprint=[${footprint.minX.toFixed(2)},${footprint.maxX.toFixed(2)}]×` +
      `[${footprint.minZ.toFixed(2)},${footprint.maxZ.toFixed(2)}]`,
  );
  for (const tile of drafts) {
    log.push(
      `Ground ${tile.id}: xz=[${tile.rect.minX.toFixed(2)}..${tile.rect.maxX.toFixed(2)}, ` +
        `${tile.rect.minZ.toFixed(2)}..${tile.rect.maxZ.toFixed(2)}] ` +
        `clips=${tile.clips.length} keys=${tile.keys.length}`,
    );
  }
  if (nKeys === 0 && drafts.length > 1) {
    const msg = 'Ground plane tiles could not place butterfly keys (clips blocking seams)';
    warnings.push(msg);
    log.push(`WARN ${msg}`);
  }

  return {
    tiles: drafts,
    connectorKey: nKeys > 0 ? spareButterflyKeyMesh(runtime, settings) : null,
    footprint,
    slabTopY,
    slabBottomY,
    log,
    warnings,
  };
}
